import {
  Bug,
  CheckCircle2,
  CircleHelp,
  Lightbulb,
  Send,
  MessageSquareText,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactElement,
  type ReactNode,
} from "react";
import {
  AlertBox,
  Avatar,
  Button,
  Checkbox,
  FileUploadCompact,
  Input,
  Select,
  Tooltip,
  TooltipProvider,
} from "@perlab/ui";

import anandAvatar from "@/assets/images/ask-anand.jpg";
import { renderZarvisMarkdown } from "../inspector/zarvisMarkdown";
import {
  askAnand,
  submitHelpRequest,
  type AskAnandMessage,
  type HelpRequestResponse,
  type HelpRequestType,
  type HelpRequestUrgency,
} from "../api/helpRequests";
import { nodeAncestry, useContextChain } from "../state/ContextChain";
import { describeError } from "../state/errorDisplay";
import { useScopeData } from "../state/useScopeData";
import { Popover } from "./Popover";

export const ASK_ANAND_MESSAGES = [
  "Need help? Let’s grab a coffee ☕",
  "Got feedback? Let’s meet at the gym 💪",
  "Found a bug? Anand has the toolkit 🧰",
  "Have an idea? Let’s give it some reps 💡",
  "Feeling stuck? Anand can spot you 🏋️",
  "Something confusing? Let’s work it out together 🤝",
] as const;

const MESSAGE_ROTATION_MS = 2_500;
export const MAX_HELP_ATTACHMENT_BYTES = 25_000_000;
const ATTACHMENT_ACCEPT = ".png,.jpg,.jpeg,.webp,.pdf,.txt";
const ATTACHMENT_TYPE_BY_EXTENSION: Record<string, string> = {
  ".png": "image/png",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".webp": "image/webp",
  ".pdf": "application/pdf",
  ".txt": "text/plain",
};

interface ActionConfig {
  type: HelpRequestType;
  title: string;
  description: string;
  icon: LucideIcon;
}

const ACTIONS: ActionConfig[] = [
  {
    type: "help",
    title: "Ask for Help",
    description: "Get help with a workflow or something that feels unclear.",
    icon: CircleHelp,
  },
  {
    type: "feedback",
    title: "Give Feedback",
    description: "Share what is working well and what could work better.",
    icon: MessageSquareText,
  },
  {
    type: "issue",
    title: "Report an Issue",
    description: "Tell Anand about a bug or unexpected behavior.",
    icon: Bug,
  },
  {
    type: "idea",
    title: "Suggest an Idea",
    description: "Propose an improvement for the Velocity Workspace.",
    icon: Lightbulb,
  },
];

const REQUEST_TYPE_OPTIONS = ACTIONS.map(({ type, title }) => ({ value: type, label: title }));
const URGENCY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
];

type PanelStep = "actions" | "form" | "success";

interface HelpFormState {
  requestType: HelpRequestType;
  subject: string;
  description: string;
  urgency: HelpRequestUrgency;
  contactMe: boolean;
  attachment?: File;
}

interface FormErrors {
  subject?: string;
  description?: string;
  attachment?: string;
}

interface SubmitError {
  title: string;
  detail: string;
}

const initialForm = (requestType: HelpRequestType = "help"): HelpFormState => ({
  requestType,
  subject: "",
  description: "",
  urgency: "medium",
  contactMe: false,
});

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => (
    typeof window !== "undefined"
    && typeof window.matchMedia === "function"
    && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ));

  useEffect(() => {
    if (typeof window.matchMedia !== "function") return undefined;
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  return reduced;
}

export function validateHelpAttachment(file: File): string | undefined {
  if (file.size > MAX_HELP_ATTACHMENT_BYTES) return "Attachment must be 25 MB or smaller.";
  const lowerName = file.name.toLowerCase();
  const extension = Object.keys(ATTACHMENT_TYPE_BY_EXTENSION).find((candidate) => lowerName.endsWith(candidate));
  const declaredType = file.type.toLowerCase();
  if (!extension || (declaredType && ATTACHMENT_TYPE_BY_EXTENSION[extension] !== declaredType)) {
    return "Use a PNG, JPG, WebP, PDF, or TXT file.";
  }
  return undefined;
}

function validateForm(form: HelpFormState): FormErrors {
  const errors: FormErrors = {};
  const subjectLength = form.subject.trim().length;
  const descriptionLength = form.description.trim().length;
  if (subjectLength === 0) errors.subject = "Enter a short subject.";
  else if (subjectLength > 160) errors.subject = "Subject must be 160 characters or fewer.";
  if (descriptionLength === 0) errors.description = "Describe how Anand can help.";
  else if (descriptionLength > 8_000) errors.description = "Description must be 8,000 characters or fewer.";
  if (form.attachment) errors.attachment = validateHelpAttachment(form.attachment);
  return errors;
}

export function AskAnandWidget({
  renderResources,
}: {
  renderResources?: (close: () => void) => ReactNode;
}): ReactElement {
  const { ready, readerScopeNodeId, fetchScoped } = useScopeData();
  const {
    scopeNodes,
    workspaces,
    clients,
    workspaceId: contextWorkspaceId,
    clientId: contextClientId,
  } = useContextChain();
  const reducedMotion = usePrefersReducedMotion();
  const [open, setOpen] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [messageIndex, setMessageIndex] = useState(0);
  const [step, setStep] = useState<PanelStep>("actions");
  const [form, setForm] = useState<HelpFormState>(() => initialForm());
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitError, setSubmitError] = useState<SubmitError | null>(null);
  const [result, setResult] = useState<HelpRequestResponse | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState<AskAnandMessage[]>([]);
  const [chatError, setChatError] = useState<string | null>(null);
  const [chatting, setChatting] = useState(false);
  const firstActionRef = useRef<HTMLButtonElement>(null);
  const subjectRef = useRef<HTMLInputElement>(null);
  const descriptionRef = useRef<HTMLTextAreaElement>(null);
  const successCloseRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const idempotencyKeyRef = useRef(crypto.randomUUID());

  useEffect(() => () => abortRef.current?.abort(), []);

  const paused = hovered || focused || open;
  useEffect(() => {
    if (reducedMotion || paused) return undefined;
    const interval = window.setInterval(() => {
      setMessageIndex((current) => (current + 1) % ASK_ANAND_MESSAGES.length);
    }, MESSAGE_ROTATION_MS);
    return () => window.clearInterval(interval);
  }, [paused, reducedMotion]);

  useEffect(() => {
    if (!open) return;
    if (step === "actions") firstActionRef.current?.focus();
    if (step === "form") subjectRef.current?.focus();
    if (step === "success") successCloseRef.current?.focus();
  }, [open, step]);

  const scopeContext = useMemo(() => {
    const currentNode = scopeNodes.find((node) => node.id === readerScopeNodeId);
    const lineage = currentNode
      ? [currentNode, ...nodeAncestry(currentNode.id, scopeNodes)]
      : [];
    const workspaceNode = lineage.find((node) => node.node_type === "workspace");
    const clientNode = lineage.find((node) => node.node_type === "client");
    const workspaceId = workspaceNode?.id || contextWorkspaceId;
    const clientId = clientNode?.id || contextClientId || undefined;
    return {
      workspaceId,
      workspaceName: workspaceNode?.name
        || workspaces.find((workspace) => workspace.id === workspaceId)?.label
        || workspaceId,
      clientId,
      clientName: clientNode?.name
        || clients.find((client) => client.id === clientId)?.name,
    };
  }, [clients, contextClientId, contextWorkspaceId, readerScopeNodeId, scopeNodes, workspaces]);

  const resetPanel = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setStep("actions");
    setForm(initialForm());
    setErrors({});
    setSubmitError(null);
    setResult(null);
    setSubmitting(false);
    setChatInput("");
    setChatMessages([]);
    setChatError(null);
    setChatting(false);
    idempotencyKeyRef.current = crypto.randomUUID();
  };

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (!nextOpen) resetPanel();
  };

  const chooseAction = (requestType: HelpRequestType) => {
    idempotencyKeyRef.current = crypto.randomUUID();
    setForm(initialForm(requestType));
    setErrors({});
    setSubmitError(null);
    setStep("form");
  };

  const handleAttachment = (files: File[]) => {
    const file = files[0];
    const attachmentError = file ? validateHelpAttachment(file) : undefined;
    setErrors((current) => ({ ...current, attachment: attachmentError }));
    setForm((current) => ({ ...current, attachment: attachmentError ? undefined : file }));
  };

  const submit = async () => {
    const nextErrors = validateForm(form);
    setErrors(nextErrors);
    setSubmitError(null);
    if (nextErrors.subject || nextErrors.description || nextErrors.attachment) {
      if (nextErrors.subject) subjectRef.current?.focus();
      else if (nextErrors.description) descriptionRef.current?.focus();
      return;
    }
    if (!ready || !readerScopeNodeId || !scopeContext.workspaceId) {
      setSubmitError({
        title: "Choose a workspace",
        detail: "Open a workspace or client and try submitting your request again.",
      });
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setSubmitting(true);
    try {
      const pathname = window.location.pathname.slice(0, 1_024);
      const response = await submitHelpRequest(fetchScoped, {
        idempotencyKey: idempotencyKeyRef.current,
        payload: {
          request_type: form.requestType,
          subject: form.subject.trim(),
          description: form.description.trim(),
          urgency: form.urgency,
          contact_me: form.contactMe,
          context: {
            page_url: `${window.location.origin}${pathname}`.slice(0, 2_048),
            route: pathname,
            workspace_id: scopeContext.workspaceId,
            workspace_name: scopeContext.workspaceName,
            client_id: scopeContext.clientId,
            client_name: scopeContext.clientName,
            app_version: __APP_VERSION__,
          },
        },
        attachment: form.attachment,
      }, controller.signal);
      if (controller.signal.aborted) return;
      setResult(response);
      setStep("success");
    } catch (error) {
      if (controller.signal.aborted) return;
      const display = describeError(error);
      setSubmitError({ title: display.title, detail: display.detail });
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
        setSubmitting(false);
      }
    }
  };

  const cancelForm = () => {
    idempotencyKeyRef.current = crypto.randomUUID();
    setForm(initialForm());
    setErrors({});
    setSubmitError(null);
    setStep("actions");
  };

  const sendChat = async (requestedMessage?: string) => {
    const message = (requestedMessage ?? chatInput).trim();
    if (!message || chatting) return;
    if (!ready || !readerScopeNodeId) {
      setChatError("Open a workspace scope before asking Anand.");
      return;
    }
    const priorHistory = chatMessages.slice(-6);
    setChatMessages((current) => [...current, { role: "user", content: message }]);
    setChatInput("");
    setChatError(null);
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setChatting(true);
    try {
      const result = await askAnand(fetchScoped, message, priorHistory, controller.signal);
      if (controller.signal.aborted) return;
      setChatMessages((current) => [...current, { role: "assistant", content: result.text }]);
    } catch (error) {
      if (controller.signal.aborted) return;
      setChatError(describeError(error).detail);
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
      setChatting(false);
    }
  };

  const closeAndRestoreFocus = (close: () => void) => {
    close();
    window.setTimeout(() => triggerRef.current?.focus(), 0);
  };

  const closeForNextSurface = (close: () => void) => {
    triggerRef.current?.focus();
    close();
  };

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
      ariaLabel="Ask Anand Help Center"
      placement="bottom-end"
      panelClassName="w-[min(23rem,calc(100vw-1rem))] max-h-[min(38rem,calc(100dvh-4rem))]"
      trigger={({ toggle }) => (
        <TooltipProvider delayMs={150}>
          <Tooltip
            side="bottom"
            align="end"
            className={open ? "hidden" : "w-72 px-3 py-2"}
            content={(
              <span className="block text-left">
                <span className="block font-semibold">Ask Anand</span>
                <span aria-hidden="true" data-state="active" className="mt-0.5 block text-[var(--color-text-secondary)]">
                  {ASK_ANAND_MESSAGES[messageIndex]}
                </span>
              </span>
            )}
          >
            <button
              ref={triggerRef}
              type="button"
              aria-label="Help"
              title="Help"
              aria-haspopup="dialog"
              aria-expanded={open}
              onClick={toggle}
              onMouseEnter={() => setHovered(true)}
              onMouseLeave={() => setHovered(false)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-surface-muted)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] motion-reduce:transition-none"
            >
              <CircleHelp size={17} aria-hidden />
            </button>
          </Tooltip>
        </TooltipProvider>
      )}
    >
      {(close) => (
        <div className="flex max-h-[min(38rem,calc(100dvh-4rem))] flex-col bg-[var(--color-surface-elevated)]">
          <header className="flex shrink-0 items-start gap-3 border-b border-[var(--color-border-subtle)] px-4 py-3">
            <Avatar
              src={anandAvatar}
              alt="Anand"
              fallback="Anand"
              size="md"
              className="shrink-0 ring-2 ring-[var(--color-accent)]"
            />
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-semibold text-[var(--color-text-primary)]">Ask Anand</h2>
              <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
                Hi! How can I help you today?
              </p>
            </div>
            <button
              type="button"
              aria-label="Close Ask Anand Help Center"
              onClick={() => closeAndRestoreFocus(close)}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-[var(--color-text-secondary)] transition-colors hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
            >
              <X size={18} aria-hidden />
            </button>
          </header>

          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            {step === "actions" && (
              <section aria-labelledby="ask-anand-actions-title">
                <h2 id="ask-anand-actions-title" className="text-sm font-semibold text-[var(--color-text-primary)]">
                  Ask about Velocity Workspace
                </h2>
                <p className="mt-1 text-xs leading-5 text-[var(--color-text-secondary)]">
                  Answers use the application handbook only.
                </p>

                <div
                  aria-label="Ask Anand conversation"
                  aria-live="polite"
                  className="mt-3 max-h-52 space-y-2 overflow-y-auto rounded-md border border-[var(--color-border-subtle)] bg-[var(--color-surface-base)] p-2"
                >
                  {chatMessages.length === 0 && (
                    <p className="px-1 py-3 text-center text-xs text-[var(--color-text-secondary)]">
                      Try “How do I open a project?” or “How does a recorded session work?”
                    </p>
                  )}
                  {chatMessages.map((message, index) => (
                    <div
                      key={`${message.role}-${index}`}
                      className={`max-w-[90%] rounded-md px-2.5 py-2 text-xs leading-5 ${message.role === "user" ? "ml-auto bg-[var(--color-accent)] text-white" : "bg-[var(--color-surface-muted)] text-[var(--color-text-primary)]"}`}
                    >
                      {message.role === "assistant" ? (
                        <div
                          className="space-y-2 [&_p]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:my-1 [&_a]:underline [&_pre]:overflow-x-auto"
                          dangerouslySetInnerHTML={{ __html: renderZarvisMarkdown(message.content) }}
                        />
                      ) : message.content}
                    </div>
                  ))}
                  {chatting && <p className="px-1 text-xs text-[var(--color-text-secondary)]">Anand is checking the handbook…</p>}
                </div>
                <form
                  className="mt-2 flex gap-2"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void sendChat();
                  }}
                >
                  <label htmlFor="ask-anand-chat" className="sr-only">Ask about Velocity Workspace</label>
                  <input
                    id="ask-anand-chat"
                    value={chatInput}
                    maxLength={2_000}
                    disabled={chatting}
                    placeholder="Ask about Velocity Workspace…"
                    onChange={(event) => setChatInput(event.target.value)}
                    className="min-w-0 flex-1 rounded-md border border-[var(--color-border-default)] bg-[var(--color-surface-base)] px-3 py-2 text-sm text-[var(--color-text-primary)] placeholder:text-[var(--color-text-disabled)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)]"
                  />
                  <Button type="submit" size="sm" disabled={!chatInput.trim()} loading={chatting} aria-label="Send to Anand">
                    <Send size={15} aria-hidden />
                  </Button>
                </form>
                {chatError && <p role="alert" className="mt-2 text-xs text-[var(--color-danger-500)]">{chatError}</p>}

                <h3 className="mt-4 text-xs font-semibold uppercase tracking-wide text-[var(--color-text-secondary)]">
                  Contact support
                </h3>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {ACTIONS.map(({ type, title, description, icon: Icon }, index) => (
                    <button
                      key={type}
                      ref={index === 0 ? firstActionRef : undefined}
                      type="button"
                      aria-label={title}
                      onClick={() => chooseAction(type)}
                      title={description}
                      className="group min-h-16 rounded-[var(--radius-md)] border border-[var(--color-border-default)] bg-[var(--color-surface-base)] p-2 text-left transition-colors hover:border-[var(--color-accent)] hover:bg-[var(--color-accent-subtle)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] motion-reduce:transition-none"
                    >
                      <span className="inline-flex size-6 items-center justify-center rounded bg-[var(--color-accent-subtle)] text-[var(--color-accent)] group-hover:bg-[var(--color-surface-elevated)]">
                        <Icon size={16} aria-hidden />
                      </span>
                      <span className="mt-1 block text-xs font-semibold text-[var(--color-text-primary)]">{title}</span>
                    </button>
                  ))}
                </div>
                {renderResources && (
                  <div className="mt-4 border-t border-[var(--color-border-subtle)] pt-3">
                    {renderResources(() => closeForNextSurface(close))}
                  </div>
                )}
              </section>
            )}

            {step === "form" && (
              <form
                aria-label="Ask Anand request form"
                className="space-y-4"
                onSubmit={(event: FormEvent<HTMLFormElement>) => {
                  event.preventDefault();
                  void submit();
                }}
              >
                <div>
                  <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">Tell Anand what you need</h2>
                  <p className="mt-1 text-xs leading-5 text-[var(--color-text-secondary)]">
                    Please avoid adding passwords, tokens, or other sensitive information.
                  </p>
                </div>

                {(errors.subject || errors.description || errors.attachment) && (
                  <AlertBox tone="danger" title="Check your request">
                    Correct the highlighted fields before submitting.
                  </AlertBox>
                )}

                <Select
                  id="ask-anand-request-type"
                  label="Request type"
                  options={REQUEST_TYPE_OPTIONS}
                  value={form.requestType}
                  disabled={submitting}
                  onChange={(event) => setForm((current) => ({
                    ...current,
                    requestType: event.target.value as HelpRequestType,
                  }))}
                />
                <Input
                  ref={subjectRef}
                  id="ask-anand-subject"
                  label="Short subject"
                  maxLength={160}
                  value={form.subject}
                  disabled={submitting}
                  error={errors.subject}
                  onChange={(event) => {
                    setForm((current) => ({ ...current, subject: event.target.value }));
                    if (errors.subject) setErrors((current) => ({ ...current, subject: undefined }));
                  }}
                />

                <div className="flex flex-col gap-1">
                  <label htmlFor="ask-anand-description" className="text-sm font-medium text-[var(--color-text-primary)]">
                    Detailed description
                  </label>
                  <textarea
                    ref={descriptionRef}
                    id="ask-anand-description"
                    rows={4}
                    maxLength={8_000}
                    value={form.description}
                    disabled={submitting}
                    aria-invalid={Boolean(errors.description)}
                    aria-describedby={errors.description ? "ask-anand-description-error" : undefined}
                    onChange={(event) => {
                      setForm((current) => ({ ...current, description: event.target.value }));
                      if (errors.description) setErrors((current) => ({ ...current, description: undefined }));
                    }}
                    className={
                      "min-h-24 resize-y rounded-md border bg-[var(--color-surface-base)] px-3 py-2 text-sm text-[var(--color-text-primary)] "
                      + "placeholder:text-[var(--color-text-disabled)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-accent)] disabled:opacity-50 "
                      + (errors.description
                        ? "border-[var(--color-danger-500)]"
                        : "border-[var(--color-border-default)] hover:border-[var(--color-border-strong)]")
                    }
                  />
                  {errors.description && (
                    <span id="ask-anand-description-error" className="text-xs text-[var(--color-danger-500)]">
                      {errors.description}
                    </span>
                  )}
                </div>

                <Select
                  id="ask-anand-urgency"
                  label="Urgency"
                  options={URGENCY_OPTIONS}
                  value={form.urgency}
                  disabled={submitting}
                  onChange={(event) => setForm((current) => ({
                    ...current,
                    urgency: event.target.value as HelpRequestUrgency,
                  }))}
                />

                <FileUploadCompact
                  label="Screenshot or attachment (optional)"
                  hint="PNG, JPG, WebP, PDF, or TXT. 25 MB maximum."
                  accept={ATTACHMENT_ACCEPT}
                  value={form.attachment ? [form.attachment] : []}
                  disabled={submitting}
                  error={errors.attachment}
                  onChange={handleAttachment}
                />

                <Checkbox
                  id="ask-anand-contact-me"
                  label="Contact me for more details"
                  checked={form.contactMe}
                  disabled={submitting}
                  onCheckedChange={(checked) => setForm((current) => ({ ...current, contactMe: checked === true }))}
                />

                {submitError && (
                  <AlertBox
                    tone="danger"
                    title={submitError.title}
                    actions={(
                      <Button type="button" variant="secondary" size="sm" loading={submitting} onClick={() => void submit()}>
                        Retry
                      </Button>
                    )}
                  >
                    {submitError.detail}
                  </AlertBox>
                )}

                <div className="flex justify-end gap-2 border-t border-[var(--color-border-subtle)] pt-4">
                  <Button type="button" variant="ghost" disabled={submitting} onClick={cancelForm}>Cancel</Button>
                  <Button type="submit" loading={submitting}>Submit</Button>
                </div>
              </form>
            )}

            {step === "success" && result && (
              <section role="status" aria-live="polite" className="flex min-h-64 flex-col items-center justify-center text-center">
                <span className="inline-flex size-16 items-center justify-center rounded-full bg-[var(--color-success-500)]/10 text-[var(--color-success-500)]">
                  <CheckCircle2 size={36} aria-hidden />
                </span>
                <h2 className="mt-5 text-lg font-semibold text-[var(--color-text-primary)]">
                  Thanks! Anand has received your request.
                </h2>
                <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
                  Keep this reference ID for follow-up.
                </p>
                <code className="mt-4 rounded-md border border-[var(--color-border-default)] bg-[var(--color-surface-subtle)] px-4 py-2 text-sm font-semibold text-[var(--color-text-primary)]">
                  {result.reference_id}
                </code>
                <Button
                  ref={successCloseRef}
                  type="button"
                  className="mt-6"
                  onClick={() => closeAndRestoreFocus(close)}
                >
                  Close
                </Button>
              </section>
            )}
          </div>
        </div>
      )}
    </Popover>
  );
}
